import { lazy, ComponentType } from 'react';

/**
 * Lazy import with automatic retry on failure
 * Fixes: "Importing a module script failed" errors from Vite chunk loading
 *
 * Usage:
 *   const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 *
 * @param importFn - Dynamic import function
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param delayMs - Delay between retries in ms (default: 1000)
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  maxRetries = 3,
  delayMs = 1000
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await importFn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Log retry attempt (only in development)
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[lazyWithRetry] Import failed (attempt ${attempt}/${maxRetries}):`,
            lastError.message
          );
        }

        // Don't wait on the last attempt
        if (attempt < maxRetries) {
          // Exponential backoff: 1000ms, 2000ms, 4000ms
          const exponentialDelay = delayMs * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, exponentialDelay));
        }
      }
    }

    // If we get here, all retries failed
    // Try one more time with a page refresh hint
    console.error(
      '[lazyWithRetry] All import retries failed. This may be due to a network issue or a new deployment. Try refreshing the page.',
      lastError
    );

    // Re-throw the last error
    throw lastError;
  });
}

/**
 * Type-safe lazy import with retry for named exports
 *
 * Usage:
 *   const MyComponent = lazyWithRetryNamed(
 *     () => import('./MyModule'),
 *     m => m.MyComponent
 *   );
 */
export function lazyWithRetryNamed<
  TModule extends Record<string, any>,
  TComponent extends ComponentType<any>
>(
  importFn: () => Promise<TModule>,
  selector: (module: TModule) => TComponent,
  maxRetries = 3,
  delayMs = 1000
): React.LazyExoticComponent<TComponent> {
  return lazy(async () => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const module = await importFn();
        return { default: selector(module) };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[lazyWithRetryNamed] Import failed (attempt ${attempt}/${maxRetries}):`,
            lastError.message
          );
        }

        if (attempt < maxRetries) {
          const exponentialDelay = delayMs * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, exponentialDelay));
        }
      }
    }

    console.error(
      '[lazyWithRetryNamed] All import retries failed. Try refreshing the page.',
      lastError
    );

    throw lastError;
  });
}
